"use client";

import { ReactElement, useEffect, useState } from "react";
import { Stack, Box, useTheme, Button } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Table as AntTable, ConfigProvider } from "antd";
import type { ColumnsType } from "antd/es/table";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./TableV3.scss";

import Label, { LabelColor } from "../label";

import { useDispatch, useSelector } from "react-redux";

import { TableProps as AntTableProps } from "antd";
import TableConfigV3, { ColumnTypes } from "./TableV3Config";
import { RootState } from "../../store";
import {
    resetSelectedRows,
    resetSelectedRowsKeys,
    updateSelectedRowKeys,
    updateSelectedRows,
} from "../../features/table/tableStore";
import evaluate from "../../util/function/evaluate";
import CustomRow from "../Table/CustomRow";
import NewRecord from "../../types/Components/Table/newRecord";
import EmptyContent from "./empty-content";
import { TableSkeletonWithoutHeader } from "./tableSkeleton";
import { useLocation, useNavigate } from "react-router-dom";

type TableProps<T> = AntTableProps<T> & {
    config: TableConfigV3;
    data: T[];
    functions?: {
        [name: string]: (
            rowData: T,
            selectionType?: string
        ) => void | Promise<boolean>;
    };
};

const TableV3 = <T extends object>({
    config,
    data,
    functions,
    ...antTableProps
}: TableProps<T>): ReactElement => {
    const [columns, setColumns] = useState<ColumnsType<T>>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [allSelected, setAllSelected] = useState(false);
    const [clientType, setClientType] = useState<string>("");
    const [delayed, setDelayed] = useState(false);
    const navigate = useNavigate();
    const { totalRecords, selectedRows, selectedRowKeys } = useSelector(
        (state: RootState) => {
            if (!config.tableId) {
                return state.table["default"];
            } else {
                return state.table[config.tableId]
                    ? state.table[config.tableId]
                    : state.table["default"];
            }
        }
    );
    const tableLoading = useSelector(
        (state: RootState) => state.table["default"].loading
    );

    const location = useLocation();

    const currentTabValue = new URLSearchParams(location.search).get("tab");
    const currentPage = new URLSearchParams(location.search).get("page");
    const currentType = new URLSearchParams(location.search).get("type");
    const currentOrderBy = new URLSearchParams(location.search).get("ob");
    const currentOrder = new URLSearchParams(location.search).get("o");

    const getQueryParam = (key: string): string | null => {
        const params = new URLSearchParams(location.search);
        return params.get(key);
    };

    const [dataSource, setDataSource] = useState(data);
    useEffect(() => {
        setDataSource(data);
    }, [data]);

    const muiTheme = useTheme();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDelayed(true);
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    const dispatch = useDispatch();

    useEffect(() => {
        clearAllSelection();
    }, [currentTabValue]);

    useEffect(() => {
        if (allSelected) {
            clearAllSelection();
        }
    }, [currentPage]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const type = params.get("type");
        if (type) {
            setClientType(type);
        }
    }, [currentType]);

    const storedSelectedKeys =
        selectedRowKeys.find(
            (row) => row.page === Number(getQueryParam("page") || 0)
        )?.data || [];

    useEffect(() => {
        // @ts-ignore
        setColumns((prev) => {
            const newColumns = [
                config.expandable?.status
                    ? {
                          title: "",
                          dataIndex: "empty",
                          render: (_: any, record: any) =>
                              !record.children && (
                                  <span style={{ visibility: "hidden" }}>
                                      <ExpandMoreIcon />
                                  </span>
                              ),
                          width: 0,
                          onCell: () => ({
                              style: {
                                  padding: "4px 8px",
                              },
                          }),
                      }
                    : null,
                ...config.columns.map((row) => {
                    if (row.condition !== undefined) {
                        const shouldIncludeColumn = evaluate(
                            row.condition,
                            dataSource[0]
                        );
                        if (!shouldIncludeColumn) {
                            return null;
                        }
                    }

                    return {
                        title: row.title,
                        dataIndex: row.dataIndex,
                        key: row.title,
                        ...(row?.ellipsis !== undefined && {
                            ellipsis: {
                                showTitle: row.ellipsis,
                            },
                        }),
                        ...(row.sorter
                            ? {
                                  sorter: row.sorter,
                                  sortOrder:
                                      getQueryParam("ob") === row.dataIndex
                                          ? getQueryParam("o") === "1"
                                              ? "ascend"
                                              : getQueryParam("o") === "0"
                                              ? "descend"
                                              : undefined
                                          : undefined,
                              }
                            : {}),
                        align: "left",
                        width: row?.width,
                        render: (text: string, record: T) => (
                            <CustomRow
                                column={row}
                                text={text}
                                record={record}
                                // @ts-ignore
                                functions={functions}
                                config={config}
                            />
                        ),
                    };
                }),
            ].filter(Boolean);

            return newColumns;
        });
    }, [
        config.expandable,
        config.columns,
        functions,
        clientType,
        currentOrder,
        currentOrderBy,
    ]);

    const dataKeys = selectedRowKeys.map((row) => row.data).flat();
    const rowSelection = {
        selectedRowKeys: dataKeys,
        onChange: (
            selectedRowKeys: number[],
            rows: [],
            info: { type: string }
        ) => {
            if (info.type === "all") {
                setSelectAll(true);
            }

            if (allSelected && info.type !== "all") {
                setAllSelected(false);
                setSelectAll(false);
            }

            if (selectedRowKeys.length === 0) {
                setSelectAll(false);
                setAllSelected(false);
            }

            dispatch(
                updateSelectedRowKeys({
                    tableId: config.tableId,
                    page: getQueryParam("page")
                        ? parseInt(getQueryParam("page") as string, 10)
                        : 0,
                    data: selectedRowKeys,
                })
            );
            dispatch(
                updateSelectedRows({
                    tableId: config.tableId,
                    page: getQueryParam("page")
                        ? parseInt(getQueryParam("page") as string, 10)
                        : 0,
                    // @ts-ignore
                    data: dataSource.filter((row) =>
                        selectedRowKeys.includes(
                            row[config.id as keyof T] as unknown as number
                        )
                    ),
                })
            );
        },
        getCheckboxProps: (record: T) => ({
            // @ts-ignore
            selected: dataKeys.includes(record[config.id]),
        }),
    };

    const clearAllSelection = () => {
        setAllSelected(false);
        setSelectAll(false);
        dispatch(resetSelectedRows({ tableId: config.tableId }));
        dispatch(resetSelectedRowsKeys({ tableId: config.tableId }));
    };

    const renderCheckboxActions = () => {
        const totalRowSelected = selectedRows.reduce(
            (sum, row) => sum + row.data.length,
            0
        );
        return (
            <Stack direction="row" gap="16px" flexWrap="wrap">
                {!allSelected &&
                selectAll &&
                (totalRecords as number) > dataSource.length ? (
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={() => {
                            setAllSelected(true);
                            dispatch(
                                updateSelectedRows({
                                    tableId: config.tableId,
                                    page: parseInt(getQueryParam("page")!, 10),
                                    data: dataSource as [],
                                })
                            );
                            dispatch(
                                updateSelectedRowKeys({
                                    tableId: config.tableId,
                                    page: parseInt(getQueryParam("page")!, 10),
                                    data: dataSource.map(
                                        (row) =>
                                            row[
                                                config.id as keyof typeof row
                                            ] as number
                                    ),
                                })
                            );
                        }}
                    >
                        Select all {totalRecords} items
                    </Button>
                ) : allSelected ? (
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={clearAllSelection}
                    >
                        Clear selection
                    </Button>
                ) : null}
                {config.checkBoxActions?.map((action, index) => (
                    <Label
                        key={index}
                        variant="soft"
                        color={action.color as LabelColor}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            padding: 2,
                            cursor: "pointer",
                            height: "48px",
                        }}
                        onClick={() => {
                            if (action.func) {
                                functions?.[action.func](
                                    // @ts-ignore
                                    selectedRows.map((row) => row.data).flat(),
                                    allSelected
                                        ? "all"
                                        : selectAll
                                        ? "page"
                                        : "single"
                                );
                            }
                        }}
                    >
                        {action.text} (
                        {allSelected ? totalRecords : totalRowSelected})
                    </Label>
                ))}
            </Stack>
        );
    };

    const customExpandIcon = ({ expanded, onExpand, record }: any) => {
        if (record.children) {
            const handleExpand = () => {
                onExpand(record, !expanded);
            };
            return (
                <span className="pointer" onClick={handleExpand}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
            );
        }
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        const params = new URLSearchParams(location.search);
        if (sorter.order) {
            params.set("o", sorter.order === "ascend" ? "1" : "0");
            params.set("ob", sorter.field);
        } else {
            params.set("o", "-1");
        }

        // window.history.replaceState(
        //     null,
        //     "",
        //     `${window.location.pathname}?${params.toString()}`
        // );

        navigate(`${location.pathname}?${params.toString()}`);
    };

    const getRowClassName = (record: NewRecord) => {
        if (
            // @ts-ignore
            record.error &&
            !record.isParent &&
            config.expandable?.status
        ) {
            return "table___errorRow highlighted-row";
        }
        // @ts-ignore

        if (record.error) {
            return "table___errorRow";
        }

        if (
            // @ts-ignore
            !record.isParent &&
            config.expandable?.status
        ) {
            return "highlighted-row";
        }
        if (config?.onRowClick) return "row___cursorPointer";
        else return "";
    };

    return (
        <div className="table___main table___mainV3">
            <TableContainer
                sx={{
                    position: "relative",
                    overflow: "unset",
                }}
            >
                {selectedRows.some((row) => row.data.length > 0) &&
                    !!config.checkBoxActions?.length && (
                        <Box
                            sx={{
                                paddingBottom: "16px",
                            }}
                        >
                            {renderCheckboxActions()}
                        </Box>
                    )}
                <ConfigProvider
                    renderEmpty={() => (
                        <EmptyContent
                            title="No Data Found"
                            description=""
                            sx={{ pt: 5, pb: 10 }}
                        />
                    )}
                    theme={{
                        components: {
                            Table: {
                                headerBg: muiTheme.palette.background.default,
                                bodySortBg: muiTheme.palette.background.default,
                                headerColor: muiTheme.palette.text.primary,
                                colorBgContainer:
                                    muiTheme.palette.background.paper,
                                colorText: muiTheme.palette.text.primary,
                                rowSelectedBg:
                                    muiTheme.palette.background.default,
                                rowSelectedHoverBg:
                                    muiTheme.palette.background.default,
                            },
                            Dropdown: {
                                colorBgElevated:
                                    muiTheme.palette.background.paper,
                            },
                        },
                    }}
                >
                    <AntTable
                        id="ant__table"
                        columns={columns}
                        rowKey={config.id}
                        dataSource={dataSource}
                        size="small"
                        onRow={config?.onRowClick}
                        pagination={false}
                        className="custom__table"
                        // @ts-ignore
                        rowSelection={
                            config.checkBox && !config.checkboxCondition
                                ? rowSelection
                                : undefined
                        }
                        // @ts-ignore
                        rowClassName={getRowClassName}
                        onChange={handleTableChange}
                        loading={{
                            indicator: <TableSkeletonWithoutHeader />,
                            spinning: config.showLoading
                                ? tableLoading
                                : !delayed,
                        }}
                        scroll={{ x: "max-content" }}
                        expandable={{
                            expandIcon: customExpandIcon,
                        }}
                        {...antTableProps}
                    />
                </ConfigProvider>
            </TableContainer>
        </div>
    );
};

export default TableV3;
